/**
 * CoachPlaybook - Allowlist Admin Panel
 * Version: 0.7.0
 * Description: Admin-only panel for managing email allowlist
 */

import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../utils/auth/authClient';

const styles = {
  container: {
    padding: '24px',
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  header: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#f1f5f9',
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b',
    marginBottom: '24px'
  },
  addRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px'
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none'
  },
  addBtn: {
    padding: '10px 20px',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    background: '#1e293b',
    borderRadius: '8px',
    border: '1px solid #334155'
  },
  email: {
    fontSize: '14px',
    color: '#e2e8f0',
    fontWeight: '500'
  },
  date: {
    fontSize: '11px',
    color: '#64748b',
    marginTop: '2px'
  },
  removeBtn: {
    padding: '6px 12px',
    background: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  empty: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '14px',
    padding: '32px'
  },
  error: {
    color: '#f87171',
    fontSize: '13px',
    marginBottom: '12px'
  },
  success: {
    color: '#34d399',
    fontSize: '13px',
    marginBottom: '12px'
  }
};

const AllowlistPanel = () => {
  const [allowlist, setAllowlist] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchAllowlist = useCallback(async () => {
    try {
      const res = await authFetch('/api/v1/admin/allowlist');
      if (res.ok) {
        const data = await res.json();
        setAllowlist(data.allowlist || []);
      } else {
        setError('Failed to load allowlist');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllowlist();
  }, [fetchAllowlist]);

  const handleAdd = async () => {
    if (!newEmail || !newEmail.includes('@')) {
      setError('Enter a valid email');
      return;
    }
    setError('');
    setSuccess('');
    try {
      const res = await authFetch('/api/v1/admin/allowlist', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail })
      });
      if (res.ok) {
        setSuccess(`Added ${newEmail}`);
        setNewEmail('');
        fetchAllowlist();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to add');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRemove = async (email) => {
    setError('');
    setSuccess('');
    try {
      const res = await authFetch('/api/v1/admin/allowlist', {
        method: 'DELETE',
        body: JSON.stringify({ email })
      });
      if (res.ok) {
        setSuccess(`Removed ${email}`);
        fetchAllowlist();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to remove');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <div style={styles.container}><div style={styles.empty}>Loading...</div></div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>Access Allowlist</h2>
      <p style={styles.subtitle}>Manage which email addresses can log in to CoachPlaybook</p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.addRow}>
        <input
          style={styles.input}
          type="email"
          placeholder="email@example.com"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <button style={styles.addBtn} onClick={handleAdd}>Add</button>
      </div>

      <div style={styles.list}>
        {allowlist.length === 0 && (
          <div style={styles.empty}>No emails in allowlist</div>
        )}
        {allowlist.map((item) => (
          <div key={item.email} style={styles.row}>
            <div>
              <div style={styles.email}>{item.email}</div>
              <div style={styles.date}>Added {new Date(item.added_at).toLocaleDateString()}</div>
            </div>
            <button style={styles.removeBtn} onClick={() => handleRemove(item.email)}>Remove</button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllowlistPanel;
